import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText, StickyNote, Mail, MailOpen, Paperclip, Plus, Trash2,
  Loader2, Download, ChevronDown, ChevronUp, Upload, Save,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  note:      { label: 'Notiz',           Icon: StickyNote, color: 'amber' },
  file:      { label: 'Datei',           Icon: FileText,   color: 'slate' },
  email_in:  { label: 'E-Mail eingegangen', Icon: MailOpen, color: 'sky'   },
  email_out: { label: 'E-Mail gesendet',    Icon: Mail,     color: 'emerald' },
};

const COLOR_CLASSES = {
  amber:   'bg-amber-50 border-amber-200 text-amber-800',
  slate:   'bg-slate-50 border-slate-200 text-slate-700',
  sky:     'bg-sky-50 border-sky-200 text-sky-800',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const formatBytes = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const DocumentsSection = ({ leadId, password }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  // Note form
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const headers = useMemo(() => ({ 'x-admin-password': password }), [password]);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/leads/${leadId}/documents`, { headers });
      if (!res.ok) throw new Error('Fehler beim Laden');
      const data = await res.json();
      setItems(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [leadId, headers]);

  useEffect(() => {
    if (leadId) fetchDocs();
  }, [leadId, fetchDocs]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const saveNote = async () => {
    if (!noteBody.trim()) return;
    setSavingNote(true);
    try {
      const res = await fetch(`${API}/admin/leads/${leadId}/notes`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: noteTitle.trim(), body: noteBody.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setNoteTitle('');
      setNoteBody('');
      setNoteOpen(false);
      fetchDocs();
    } catch (err) {
      alert(`Notiz konnte nicht gespeichert werden:\n${err.message}`);
    } finally {
      setSavingNote(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('Datei zu groß (max 10 MB).');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name);
      const res = await fetch(`${API}/admin/leads/${leadId}/documents/upload`, {
        method: 'POST',
        headers, // do NOT set Content-Type — browser adds boundary
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      fetchDocs();
    } catch (err) {
      alert(`Upload fehlgeschlagen:\n${err.message}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Eintrag löschen?')) return;
    try {
      const res = await fetch(`${API}/admin/leads/${leadId}/documents/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchDocs();
    } catch (err) {
      alert(`Löschen fehlgeschlagen: ${err.message}`);
    }
  };

  const downloadFile = async (id, filename) => {
    try {
      const res = await fetch(`${API}/admin/leads/${leadId}/documents/${id}/download`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'datei';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Download fehlgeschlagen: ${err.message}`);
    }
  };

  const downloadAttachment = async (docId, idx, filename) => {
    try {
      const res = await fetch(
        `${API}/admin/leads/${leadId}/documents/${docId}/attachments/${idx}`,
        { headers },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `anhang-${idx}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Anhang-Download fehlgeschlagen: ${err.message}`);
    }
  };

  return (
    <section data-testid="lead-documents-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700">
          Dokumente &amp; E-Mails
          {items.length > 0 && (
            <span className="ml-2 text-xs font-normal text-slate-400 normal-case">
              ({items.length})
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <label
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 bg-white text-slate-700 hover:border-slate-400 cursor-pointer"
            data-testid="doc-upload-label"
          >
            {uploading ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Upload size={13} />
            )}
            Datei hochladen
            <input
              type="file"
              className="hidden"
              accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*,text/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadFile(f);
                e.target.value = ''; // allow re-uploading same file
              }}
              data-testid="doc-upload-input"
              disabled={uploading}
            />
          </label>
          <button
            type="button"
            onClick={() => setNoteOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
            data-testid="doc-note-toggle"
          >
            <Plus size={13} />
            Notiz hinzufügen
          </button>
        </div>
      </div>

      {/* Note form */}
      {noteOpen && (
        <div
          className="bg-amber-50/60 border border-amber-200 rounded-lg p-3 space-y-2 mb-4"
          data-testid="doc-note-form"
        >
          <Input
            placeholder="Titel (optional, z. B. Telefonat 14:30)"
            value={noteTitle}
            onChange={(e) => setNoteTitle(e.target.value)}
            className="bg-white"
            data-testid="doc-note-title"
          />
          <Textarea
            placeholder="Inhalt der Notiz — was wurde besprochen, vereinbart, beobachtet?"
            value={noteBody}
            onChange={(e) => setNoteBody(e.target.value)}
            className="min-h-[90px] bg-white"
            data-testid="doc-note-body"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setNoteOpen(false); setNoteTitle(''); setNoteBody(''); }}
              className="text-slate-600"
              data-testid="doc-note-cancel"
            >
              Abbrechen
            </Button>
            <Button
              size="sm"
              onClick={saveNote}
              disabled={savingNote || !noteBody.trim()}
              className="bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="doc-note-save"
            >
              {savingNote ? <Loader2 size={13} className="animate-spin mr-1" /> : <Save size={13} className="mr-1" />}
              Speichern
            </Button>
          </div>
        </div>
      )}

      {/* Timeline */}
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 size={20} className="animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <div
          className="border border-dashed border-slate-200 rounded-lg p-6 text-center text-sm text-slate-400"
          data-testid="doc-empty"
        >
          Keine Notizen, Dateien oder E-Mails hinterlegt.<br />
          Tipp: Telefonnotizen schnell mit „Notiz hinzufügen" festhalten.
        </div>
      ) : (
        <div className="space-y-2" data-testid="doc-list">
          {items.map((item) => {
            const meta = TYPE_META[item.type] || TYPE_META.note;
            const Icon = meta.Icon;
            const expanded = expandedIds.has(item.id);
            const isEmail = item.type === 'email_in' || item.type === 'email_out';
            const isNote = item.type === 'note';
            const isFile = item.type === 'file';

            const subject = item.subject || item.title || (isFile ? item.filename : 'Notiz');
            const preview = (item.body || '').slice(0, 140);

            return (
              <div
                key={item.id}
                className="border border-slate-200 rounded-lg bg-white overflow-hidden"
                data-testid={`doc-item-${item.id}`}
              >
                <div className="flex items-start gap-3 p-3">
                  <div className={`shrink-0 w-8 h-8 rounded-md border flex items-center justify-center ${COLOR_CLASSES[meta.color]}`}>
                    <Icon size={15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                        {meta.label}
                      </span>
                      <span className="text-xs text-slate-400">
                        {formatDate(item.message_date || item.created_at)}
                      </span>
                      {isFile && (
                        <span className="text-xs text-slate-400">
                          {formatBytes(item.file_size)}
                        </span>
                      )}
                      {isEmail && item.attachments?.length > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-slate-500">
                          <Paperclip size={11} /> {item.attachments.length}
                        </span>
                      )}
                    </div>
                    <div className="font-medium text-sm text-slate-900 mt-0.5 truncate">
                      {subject}
                    </div>
                    {isEmail && (
                      <div className="text-xs text-slate-500 mt-0.5 truncate">
                        {item.type === 'email_in'
                          ? `Von: ${item.from_addr || '—'}`
                          : `An: ${(item.to_addrs || []).join(', ') || '—'}`}
                      </div>
                    )}
                    {(isNote || isEmail) && preview && !expanded && (
                      <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {preview}
                        {item.body && item.body.length > 140 ? '…' : ''}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {(isNote || isEmail) && item.body && (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(item.id)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
                        title={expanded ? 'Einklappen' : 'Ausklappen'}
                        data-testid={`doc-toggle-${item.id}`}
                      >
                        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    )}
                    {isFile && (
                      <button
                        type="button"
                        onClick={() => downloadFile(item.id, item.filename)}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
                        title="Herunterladen"
                        data-testid={`doc-download-${item.id}`}
                      >
                        <Download size={14} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => deleteItem(item.id)}
                      className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                      title="Löschen"
                      data-testid={`doc-delete-${item.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {expanded && (isNote || isEmail) && (
                  <div className="border-t border-slate-100 bg-slate-50 px-3 py-3 text-sm text-slate-700 whitespace-pre-wrap" data-testid={`doc-body-${item.id}`}>
                    {item.body || '—'}
                    {isEmail && item.attachments?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                          Anhänge
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {item.attachments.map((a, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => a.has_data && downloadAttachment(item.id, idx, a.filename)}
                              disabled={!a.has_data}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs ${
                                a.has_data
                                  ? 'border-slate-300 bg-white text-slate-700 hover:border-slate-400 cursor-pointer'
                                  : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                              }`}
                              title={a.has_data ? `Herunterladen (${formatBytes(a.size)})` : 'Anhang zu groß und nicht gespeichert'}
                            >
                              <Paperclip size={11} />
                              {a.filename}
                              <span className="text-slate-400">·</span>
                              <span className="text-slate-400">{formatBytes(a.size)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default DocumentsSection;
